import { ReactNode, useId } from 'react';

import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

export interface ToggleFieldProps {
  label: ReactNode;
  /** What turning it on does, in one line. */
  description?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/**
 * A setting that is on or off: its name and what it does at the start, the
 * switch at the end. Stack several in a `divide-y` list. Used by both catalog
 * editors (Active, Default, the visualization rules).
 */
export function ToggleField({ label, description, checked, onCheckedChange, disabled, id, className }: ToggleFieldProps) {
  const autoId = useId();
  const switchId = id || autoId;
  const descriptionId = `${switchId}-description`;

  return (
    <div className={cn('flex items-start justify-between gap-4 py-3', className)}>
      <div className="min-w-0">
        <Label htmlFor={switchId} className="text-sm font-medium">
          {label}
        </Label>
        {description && (
          <p id={descriptionId} className="mt-1 text-xs text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      <Switch
        id={switchId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-describedby={description ? descriptionId : undefined}
        className="mt-0.5"
      />
    </div>
  );
}
